import React, {useEffect, useState, useCallback} from 'react';
import {Button} from '../bootstrap';
import DropdownMenu from '../common/DropdownMenu.jsx';
import {fetchJson} from '../../helpers/index.js';
import SavePresetModalTrigger from './preset_manager/SavePresetModalTrigger.jsx';
import ManagePresetsModalTrigger from './preset_manager/ManagePresetsModalTrigger.jsx';

import { datagrid_create_preset_path } from '~/routes';

const ACTIVE_STYLE =  {backgroundColor: '#5c6276', color: '#FFF'}
const NO_FILTER = ['none', I18n.t('none').toUpperCase()]

const FilterButton = ({children, ...rest}) =>  <Button type='default' size='sm' {...rest}>{children}</Button>

const PresetMenuItem = ({preset, handleItemClick}) => <li className='dropdown-item'><a href="#" onClick={() => handleItemClick([preset.id, preset.name])}>{preset.name}</a></li>

const MenuContent = ({searchTerm, handleTermChange, presets, handleItemClick}) => {
  return <>
    <input className='form-control input-sm' style={{width: 'unset', margin: '0 0.5rem 0.5rem 0.5rem'}} defaultValue={searchTerm} autoFocus placeholder={I18n.t('search')} type='text' onChange={handleTermChange} />
    {presets.length == 0 ?
      <span className='description'>{I18n.t('no_presets')}</span> :
      presets.map(preset => <PresetMenuItem key={preset.id} preset={preset} handleItemClick={handleItemClick}/>)
    }
  </>
}

const PresetSearchMenu = ({presets, handleItemClick}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredPresets, setFilteredPresets] = useState(presets);
  const handleTermChange = (event) => {
    setSearchTerm(event.target.value)
  }

  useEffect(() => {
    const filtered = _.isEmpty(searchTerm) ? presets : presets.filter((preset) =>
      preset.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredPresets(filtered)
  }, [searchTerm]);

  return <MenuContent
    searchTerm={searchTerm}
    handleTermChange={handleTermChange}
    presets={filteredPresets}
    handleItemClick={handleItemClick}
  />;
}

const PresetSettingMenu = ({permissions, presets, setPresets, saveState, restoreState, resetState, context_name, datagrid_id}) => {
  return <div>
    <SavePresetModalTrigger {...{permissions, presets, saveState, context_name}}/>
    <ManagePresetsModalTrigger {...{permissions, presets, setPresets, restoreState, resetState, context_name, datagrid_id}} displayPreset={() => {}}/>
  </div>
}

export const restorePreset = (preset, gridRef) => {
  gridRef.current.api.applyColumnState({
    state: preset.column_state,
    applyOrder: true,
  });
  gridRef.current.api.setFilterModel(preset.filter_model);
  gridRef.current.api.setGridOption('pivotMode', preset.column_state.some(a => a.pivot));
}

export const resetPreset = (gridRef) => {
  gridRef.current.api.resetColumnState();
  gridRef.current.api.setFilterModel([]);
  gridRef.current.api.setGridOption('pivotMode', false);
}

export function PresetManager({datagrid, context, gridRef}) {
  const { permissions, context_name, programme_id, project_id } = context;
  const { default_preset_id } = datagrid;

  const [presets, setPresets] = useState(datagrid.presets.filter(p => p.type === 'GenericPreset'));
  const [mostRecent, setMostRecent] = useState(null)

  const defaultActiveItem = presets.find(v =>
    v.id === default_preset_id && (programme_id === project_id ? v.default : v.default_in_project_context)
  )
  const [activeItem, setActiveItem] = useState(defaultActiveItem ? [defaultActiveItem.id, defaultActiveItem.name] : NO_FILTER)

  const handleItemClick = (item) => {
    item == NO_FILTER ? resetState() : restoreState(presets.find(v => v.id == item[0]));

    // Display the previous item to easily switch between two
    if (activeItem && activeItem != NO_FILTER) {
      setMostRecent([...activeItem]);
    } else if (mostRecent && item[0] == mostRecent[0] ) {
      setMostRecent(null);
    }
    setActiveItem(item == NO_FILTER ? NO_FILTER : [...item])
  }

  const activeStyle = (id) => activeItem[0] == id ? ACTIVE_STYLE : {}

  const restoreState = useCallback((preset) => {
    restorePreset(preset, {current: {api: gridRef.current.api}});
  });

  const resetState = useCallback(() => {
    resetPreset(gridRef);
  });

  const saveState = useCallback((formData, cb) => {
    const newPreset = {
      ...formData,
      column_state: gridRef.current.api.getColumnState(),
      filter_model: gridRef.current.api.getFilterModel()
    }
    fetchJson(
      datagrid_create_preset_path({datagrid_id: datagrid.id, context_name: context_name, _options: true}),
      {body: JSON.stringify({...newPreset}), method: 'POST'}
    ).then(res => {
      cb(res);
      if (!res.errors) {
        if (formData.preset_id) {
          setPresets(presets.map(preset => preset.id == formData.preset_id ? {...preset, column_state: newPreset.column_state, filter_model: newPreset.filter_model} : preset));
        } else {
          setPresets([...presets, {...newPreset, id: res.id}]);
        }
      }
    })
  });

  const TruncateText = ({ text, limit = 16 }) => (
    <>{text.length > limit ? text.substring(0, limit) + '...' : text}</>
  );

  const onOutsideClick = (e) => {
    let managePresetsBTN = $('#manage-presets-btn');
    if (managePresetsBTN.length) {
      setTimeout(() => {
        let shouldClose = !managePresetsBTN[0].contains(e.target) && $('.dropdown-menu-separator').length && !$('.modal-backdrop').length;
        if (shouldClose) managePresetsBTN[0].click()
      }, 10);
    }
  }

  useEffect(() => {
    window.addEventListener('click', onOutsideClick)
    return () => {window.removeEventListener('click', onOutsideClick)}
  }, [])

  return <div>
    <div style={{marginTop: 0, marginLeft: 5}} className='description'>{I18n.t('presets').toUpperCase()}</div>
    <div className='btn-group' role='group'>
      <FilterButton style={activeStyle(NO_FILTER[0])} onClick={() => handleItemClick(NO_FILTER)}>{NO_FILTER[1]}</FilterButton>
      {mostRecent && <FilterButton onClick={() => handleItemClick(mostRecent)}>{<TruncateText text={mostRecent[1]}/>}</FilterButton>}

      <div className='btn-group' role='group'>
        <DropdownMenu menuItems={<PresetSearchMenu presets={presets} handleItemClick={handleItemClick}/>}>
          <FilterButton
            style={{...activeStyle(activeItem != NO_FILTER && activeItem[0]), ...(permissions.presets_manage ? {borderRadius: 0} : {borderTopLeftRadius: 0, borderBottomLeftRadius: 0})}}
          >
            {activeItem == NO_FILTER ? '•••' : <>{<TruncateText text={activeItem[1]}/>}<span className='caret' style={{borderColor: '#FFF'}}/></>}
          </FilterButton>
        </DropdownMenu>
      </div>

      { permissions.presets_manage &&
        <div className='btn-group' role='group'>
          <DropdownMenu
            menuItems={<PresetSettingMenu {...{permissions, presets, setPresets, saveState, restoreState, resetState, context_name, datagrid_id: datagrid.id}}/>}
            className={'dropdown-menu dropdown-menu-separator'}
            closeOnOutsideClick={false}
          >
            <FilterButton style={{borderTopLeftRadius: 0, borderBottomLeftRadius: 0}} id={'manage-presets-btn'}>
              <span className='fa fa-cog' style={{borderColor: '#FFF'}}/>
            </FilterButton>
          </DropdownMenu>
        </div>
      }
    </div>
  </div>
}
